/**
 * Client identity — what this app build is, sent to the backend on every
 * request as X-Client-* headers so we can track which frontend versions are
 * live in the field (and reason about backend backward-compatibility).
 *
 * Version source of truth:
 *   • Native (iOS/Android): the installed app version + build number, read from
 *     the native project (Info.plist / build.gradle) via @capacitor/app — the
 *     real version on the device, regardless of what the JS bundle thinks.
 *   • Web: the version baked in at build time from package.json (__APP_VERSION__).
 * The git commit (__GIT_SHA__) is injected by vite.config.ts in both cases.
 */
import { Capacitor } from "@capacitor/core";

// Injected by Vite's `define` at build time. Declared here for tsc; guarded
// with typeof so tests / unbundled runs don't hit a ReferenceError.
declare const __APP_VERSION__: string;
declare const __GIT_SHA__: string;

const BUILD_VERSION = typeof __APP_VERSION__ !== "undefined" ? __APP_VERSION__ : "0.0.0";
const GIT_SHA = typeof __GIT_SHA__ !== "undefined" ? __GIT_SHA__ : "unknown";

const info = {
  version: BUILD_VERSION,          // overwritten with the native version below
  build: "",                       // native build number (empty on web)
  platform: Capacitor.getPlatform(), // "ios" | "android" | "web"
  commit: GIT_SHA,
};

// On native, replace the injected version with the actual installed one. This
// runs once at startup; until it resolves (a few ms) the header falls back to
// the build-time version, which is close enough.
if (Capacitor.isNativePlatform()) {
  void (async () => {
    try {
      const { App } = await import("@capacitor/app");
      const native = await App.getInfo();
      if (native?.version) info.version = native.version;
      if (native?.build) info.build = String(native.build);
    } catch {
      /* @capacitor/app unavailable — keep the injected version */
    }
  })();
}

/** The current client identity (version/build/platform/commit). */
export function clientInfo() {
  return { ...info };
}

/** Headers announcing this client to the backend. Attached to every request. */
export function clientHeaders(): Record<string, string> {
  const h: Record<string, string> = {
    "X-Client-Version": info.version,
    "X-Client-Platform": info.platform,
    "X-Client-Commit": info.commit,
  };
  if (info.build) h["X-Client-Build"] = info.build;
  return h;
}
